import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

export const getSupplierOrdersList = createAsyncThunk(
    'supplierOrders/getSupplierOrdersList',
    async ({ accountId, token, search, start, length, statusId, supplierId }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/supplierOrders/list?accountId=${accountId}&start=${start}&length=${length}&draw=1`;

        if (search && search.length > 0) {
            url += `&search[value]=${encodeURIComponent(search)}`;
        } else {
            url += `&search[value]=`;
        }

        if (statusId) {
            url += `&statusId=${statusId}`;
        }
        if (supplierId) {
            url += `&supplierId=${supplierId}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero degli ordini fornitore');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const getSupplierOrderDetails = createAsyncThunk(
    'supplierOrders/getSupplierOrderDetails',
    async ({ supplierOrderId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/supplierOrders?supplierOrderId=${supplierOrderId}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            }
        });

        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante il recupero dei dettagli dell\'ordine');
        }

        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const createSupplierOrder = createAsyncThunk(
    'supplierOrders/createSupplierOrder',
    async ({ supplierOrder, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/supplierOrders`;
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ supplierOrder })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante la creazione dell\'ordine');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const updateSupplierOrder = createAsyncThunk(
    'supplierOrders/updateSupplierOrder',
    async ({ supplierOrder, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/supplierOrders`;
        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ supplierOrder })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'aggiornamento dell\'ordine');
        }
        const data = await response.json();
        return data.data;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

export const deleteSupplierOrder = createAsyncThunk(
    'supplierOrders/deleteSupplierOrder',
    async ({ supplierOrderId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/supplierOrders`;
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'auth-token': token
            },
            body: JSON.stringify({ supplierOrderId })
        });
        if (!response.ok) {
            const errorData = await response.json();
            return rejectWithValue(errorData.message || 'Errore durante l\'eliminazione dell\'ordine');
        }
        return supplierOrderId;
    } catch (error) {
        return rejectWithValue(error.message);
    }
});

const supplierOrdersSlice = createSlice({
    name: 'supplierOrders',
    initialState: {
        data: null,
        loading: false,
        error: null,
        orderDetails: null,
        loadingDetails: false,
        errorDetails: null,
        createLoading: false,
        createError: null,
        updateLoading: false,
        updateError: null,
        deleteLoading: false,
        deleteError: null
    },
    reducers: {
        resetOrderDetails: (state) => {
            state.orderDetails = null;
            state.loadingDetails = false;
            state.errorDetails = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getSupplierOrdersList
            .addCase(getSupplierOrdersList.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getSupplierOrdersList.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(getSupplierOrdersList.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            // getSupplierOrderDetails
            .addCase(getSupplierOrderDetails.pending, (state) => {
                state.loadingDetails = true;
                state.errorDetails = null;
            })
            .addCase(getSupplierOrderDetails.fulfilled, (state, action) => {
                state.loadingDetails = false;
                state.orderDetails = action.payload;
            })
            .addCase(getSupplierOrderDetails.rejected, (state, action) => {
                state.loadingDetails = false;
                state.errorDetails = action.payload;
            })
            // createSupplierOrder
            .addCase(createSupplierOrder.pending, (state) => {
                state.createLoading = true;
                state.createError = null;
            })
            .addCase(createSupplierOrder.fulfilled, (state) => {
                state.createLoading = false;
            })
            .addCase(createSupplierOrder.rejected, (state, action) => {
                state.createLoading = false;
                state.createError = action.payload;
            })
            // updateSupplierOrder
            .addCase(updateSupplierOrder.pending, (state) => {
                state.updateLoading = true;
                state.updateError = null;
            })
            .addCase(updateSupplierOrder.fulfilled, (state) => {
                state.updateLoading = false;
            })
            .addCase(updateSupplierOrder.rejected, (state, action) => {
                state.updateLoading = false;
                state.updateError = action.payload;
            })
            // deleteSupplierOrder
            .addCase(deleteSupplierOrder.pending, (state) => {
                state.deleteLoading = true;
                state.deleteError = null;
            })
            .addCase(deleteSupplierOrder.fulfilled, (state) => {
                state.deleteLoading = false;
            })
            .addCase(deleteSupplierOrder.rejected, (state, action) => {
                state.deleteLoading = false;
                state.deleteError = action.payload;
            });
    }
});

export const { resetOrderDetails } = supplierOrdersSlice.actions;

export const selectSupplierOrdersData = (state) => state.supplierOrders.data;
export const selectSupplierOrdersLoading = (state) => state.supplierOrders.loading;
export const selectSupplierOrdersError = (state) => state.supplierOrders.error;
export const selectSupplierOrderDetails = (state) => state.supplierOrders.orderDetails;
export const selectSupplierOrderDetailsLoading = (state) => state.supplierOrders.loadingDetails;
export const selectSupplierOrderDetailsError = (state) => state.supplierOrders.errorDetails;
export const selectSupplierOrderCreateLoading = (state) => state.supplierOrders.createLoading;
export const selectSupplierOrderCreateError = (state) => state.supplierOrders.createError;
export const selectSupplierOrderUpdateLoading = (state) => state.supplierOrders.updateLoading;
export const selectSupplierOrderUpdateError = (state) => state.supplierOrders.updateError;
export const selectSupplierOrderDeleteLoading = (state) => state.supplierOrders.deleteLoading;
export const selectSupplierOrderDeleteError = (state) => state.supplierOrders.deleteError;

export default supplierOrdersSlice.reducer;
